import { useCallback, useEffect, useRef } from 'react';

import endCallUrl from '../sounds/end-call.ogg';
import toolCallUrl from '../sounds/tool-call.mp3';

export interface VoiceSoundEffects {
  playEndCall(): void;
  startToolLoop(): void;
  stopToolLoop(): void;
}

const END_CALL_VOLUME = 0.6;
const TOOL_LOOP_VOLUME = 0.25;
const FADE_IN_MS = 200;
const FADE_OUT_MS = 350;
const RAMP_STEP_MS = 20;

function rampVolume(
  audio: HTMLAudioElement,
  from: number,
  to: number,
  durationMs: number,
  onComplete?: () => void,
): ReturnType<typeof setInterval> {
  const steps = Math.max(1, Math.round(durationMs / RAMP_STEP_MS));
  const delta = (to - from) / steps;
  let current = from;
  let step = 0;
  audio.volume = from;

  const timer = setInterval(() => {
    step += 1;
    current += delta;
    if (step >= steps) {
      audio.volume = to;
      clearInterval(timer);
      onComplete?.();
      return;
    }
    audio.volume = Math.max(0, Math.min(1, current));
  }, RAMP_STEP_MS);

  return timer;
}

/**
 * Client-side sound effects for the live voice call overlay (D-81).
 *
 * - `playEndCall` — one-shot farewell chime on every real call end.
 * - `startToolLoop` / `stopToolLoop` — ambient loop while tools run and
 *   the assistant is not speaking; volume ramps avoid jarring cuts.
 */
export function useVoiceSoundEffects(): VoiceSoundEffects {
  const endCallAudioRef = useRef<HTMLAudioElement | null>(null);
  const toolLoopAudioRef = useRef<HTMLAudioElement | null>(null);
  const rampTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const loopRunningRef = useRef(false);

  const clearRamp = useCallback(() => {
    if (rampTimerRef.current != null) {
      clearInterval(rampTimerRef.current);
      rampTimerRef.current = null;
    }
  }, []);

  const getEndCallAudio = useCallback((): HTMLAudioElement => {
    if (!endCallAudioRef.current) {
      const audio = new Audio(endCallUrl);
      audio.preload = 'auto';
      endCallAudioRef.current = audio;
    }
    return endCallAudioRef.current;
  }, []);

  const getToolLoopAudio = useCallback((): HTMLAudioElement => {
    if (!toolLoopAudioRef.current) {
      const audio = new Audio(toolCallUrl);
      audio.preload = 'auto';
      audio.loop = true;
      toolLoopAudioRef.current = audio;
    }
    return toolLoopAudioRef.current;
  }, []);

  const playEndCall = useCallback(() => {
    clearRamp();
    const audio = getEndCallAudio();
    audio.currentTime = 0;
    audio.volume = END_CALL_VOLUME;
    void audio.play().catch(() => {
      // Safari < iOS 17 may not decode Ogg Vorbis — fail silently.
    });
  }, [clearRamp, getEndCallAudio]);

  const startToolLoop = useCallback(() => {
    if (loopRunningRef.current) return;
    loopRunningRef.current = true;
    clearRamp();

    const audio = getToolLoopAudio();
    audio.currentTime = 0;
    audio.volume = 0;
    void audio.play().catch(() => {
      loopRunningRef.current = false;
    });
    rampTimerRef.current = rampVolume(audio, 0, TOOL_LOOP_VOLUME, FADE_IN_MS, () => {
      rampTimerRef.current = null;
    });
  }, [clearRamp, getToolLoopAudio]);

  const stopToolLoop = useCallback(() => {
    if (!loopRunningRef.current && rampTimerRef.current == null) return;
    loopRunningRef.current = false;
    clearRamp();

    const audio = toolLoopAudioRef.current;
    if (!audio) return;

    const from = audio.volume;
    if (from <= 0) {
      audio.pause();
      audio.currentTime = 0;
      return;
    }

    rampTimerRef.current = rampVolume(audio, from, 0, FADE_OUT_MS, () => {
      rampTimerRef.current = null;
      audio.pause();
      audio.currentTime = 0;
    });
  }, [clearRamp]);

  useEffect(() => {
    return () => {
      clearRamp();
      loopRunningRef.current = false;
      endCallAudioRef.current?.pause();
      toolLoopAudioRef.current?.pause();
    };
  }, [clearRamp]);

  return { playEndCall, startToolLoop, stopToolLoop };
}
